// The location page's one form: what it holds, where it starts, and the body
// it saves.
//
// Two tables, one save. The name is a column on `locations`; the posting
// file's path and truncation are columns on the location's `streams` row.
// PATCH /v1/locations/{id} takes both — the stream half nested under
// `stream`, carrying its own id, because a location's stream is a convention
// of the data rather than a guarantee of the schema (see
// crates/ewt-server/src/locations.rs) — and writes them in one transaction.
//
// The service account is deliberately NOT here: it isn't writable through
// this endpoint at all (see service-config-card.tsx) — it's bound only by
// downloading the posting service's configuration, which mints a fresh
// credential. Putting it in this form would let the download dirty the one
// save bar for an edit the operator never made.

import { z } from "zod";
import type { LocationListEntry, PatchLocation } from "@/api/types";

export const configureSchema = z
    .object({
        name: z.string().trim().min(1, "Name is required"),
        // Optional too — see posting-path.ts. A location can exist before
        // anyone has read the path off the POS, and this form must not block
        // on it.
        filePath: z.string().trim(),
        truncationEnabled: z.boolean(),
        truncationMaxRows: z.union([z.number(), z.literal("")]),
    })
    .superRefine((values, ctx) => {
        if (!values.truncationEnabled) return;
        if (values.truncationMaxRows === "") {
            ctx.addIssue({
                code: "custom",
                path: ["truncationMaxRows"],
                message: "Required when truncation is enabled",
            });
            return;
        }
        if (values.truncationMaxRows < 100) {
            ctx.addIssue({ code: "custom", path: ["truncationMaxRows"], message: "Must be at least 100" });
        }
    });

export type ConfigureValues = z.infer<typeof configureSchema>;

export function toDefaults(location: LocationListEntry): ConfigureValues {
    return {
        name: location.name,
        filePath: location.stream.filePath,
        truncationEnabled: location.stream.truncationEnabled,
        truncationMaxRows: location.stream.truncationMaxRows ?? "",
    };
}

export function toBody(values: ConfigureValues, streamId: string): PatchLocation {
    return {
        name: values.name,
        stream: {
            streamId,
            filePath: values.filePath,
            truncationEnabled: values.truncationEnabled,
            truncationMaxRows: values.truncationMaxRows === "" ? null : values.truncationMaxRows,
        },
    };
}
